import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/layout/DataTable";
import { EmptyState } from "../../components/layout/EmptyState";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { SectionIntro } from "../../components/layout/SectionIntro";
import { apiFetch, buildApiUrl } from "../../lib/api";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPct,
  formatSignedDays,
  formatSignedInt,
  sumTruthStageUnits,
  summarizeDiffReasons,
} from "../../lib/format";
import {
  clearPendingIdempotencyKey,
  getOrCreatePendingIdempotencyKey,
  type PendingIdempotencyEntry,
} from "../../lib/idempotency";
import { parseNumericDraft } from "../../lib/numericDraft";
import {
  readBooleanParam,
  readNullableStringParam,
  readStringParam,
} from "../../lib/url-state";
import type {
  DashboardResponse,
  DemandSeriesResponse,
  ProjectionResponse,
  RecommendationRow,
  RunsListResponse,
  RunDiffResponse,
  SkuCostHistoryResponse,
  SkuFeedbackDetailResponse,
  SkuInboundPipeline,
  SkuRunDiffDetail,
  SupplierFeedbackListResponse,
  SupplierFeedbackRow,
  SupplierTerm,
  RecommendationRunSummary,
  ProjectionRow,
  DemandSeriesRow,
  FeedbackOverviewResponse,
} from "../../lib/types";
import { useDomainVersion, useInvalidation } from "../../app/invalidation";

type PlanningWorkspaceState = {
  loading: boolean;
  error: string | null;
  feedbackWarnings: string[];
  runBuildWarnings: string[];
  requestedRunId: string | null;
  activeRunId: string | null;
  isRunSwitching: boolean;
  compareRunId: string;
  tier: string;
  risk: string;
  supplier: string;
  blockedOnly: boolean;
  scenario: string;
  selectedSku: string | null;
  cashLimit: number;
  currentCashDraft: string;
  currentCashValid: boolean;
  runs: RecommendationRunSummary[];
  rows: RecommendationRow[];
  runDiff: RunDiffResponse | null;
  selectedRunDiffDetail: SkuRunDiffDetail | null;
  demandSeries: DemandSeriesRow[];
  projectionRows: ProjectionRow[];
  dashboardWarnings: string[];
  dataFreshnessSeconds: number | null;
  runSourceMode: string | null;
  runHealthStatus: string | null;
  freshSkuCount: number | null;
  blockedSkuCount: number | null;
  blockedASkuCount: number | null;
  feedbackOverview: FeedbackOverviewResponse | null;
  skuFeedback: SkuFeedbackDetailResponse | null;
  supplierFeedback: SupplierFeedbackRow[];
  skuInboundPipeline: SkuInboundPipeline | null;
  skuCostHistory: SkuCostHistoryResponse | null;
  supplierTerms: SupplierTerm | null;
  handleRunPlanning: () => Promise<void>;
  handleWhatIf: () => Promise<void>;
  setCashLimit: (value: number) => void;
  setCurrentCashDraft: (value: string) => void;
  setScenario: (value: string) => void;
  updateFilters: (updates: Record<string, string | boolean | null>) => void;
  selectSku: (sku: string | null) => void;
};

type DashboardSnapshot = {
  runId: string;
  rows: RecommendationRow[];
  warnings: string[];
  dataFreshnessSeconds: number | null;
  runSourceMode: string | null;
  runHealthStatus: string | null;
  freshSkuCount: number | null;
  blockedSkuCount: number | null;
  blockedASkuCount: number | null;
};

type SearchValue = string | number | boolean | null | undefined;
const RUN_BUILD_WARNING_MESSAGE =
  "Planning run was created, but the dashboard refresh failed. Retrying the same action is safe.";

function applySearchParamUpdates(searchParams: URLSearchParams, updates: Record<string, SearchValue>) {
  const next = new URLSearchParams(searchParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "" || value === false) {
      next.delete(key);
      return;
    }
    next.set(key, String(value));
  });
  return next;
}

function buildDashboardRequestKey(params: {
  runId: string | null;
  tier: string;
  risk: string;
  supplier: string;
  blockedOnly: boolean;
  planningVersion: number;
}) {
  return JSON.stringify({
    runId: params.runId ?? "__latest__",
    tier: params.tier,
    risk: params.risk,
    supplier: params.supplier,
    blockedOnly: params.blockedOnly,
    planningVersion: params.planningVersion,
  });
}

function buildRunDiffRequestKey(params: {
  runId: string;
  compareRunId: string;
  tier: string;
  risk: string;
  supplier: string;
  blockedOnly: boolean;
}) {
  return JSON.stringify({
    runId: params.runId,
    compareRunId: params.compareRunId,
    tier: params.tier,
    risk: params.risk,
    supplier: params.supplier,
    blockedOnly: params.blockedOnly,
  });
}

function normalizeSelectedSku(rows: RecommendationRow[], preferredSku: string | null) {
  if (preferredSku && rows.some((row) => row.sku === preferredSku)) {
    return preferredSku;
  }
  const ranked = [...rows].sort((a, b) => {
    const aBlocked = a.blocked ? 1 : 0;
    const bBlocked = b.blocked ? 1 : 0;
    if (aBlocked !== bBlocked) {
      return aBlocked - bBlocked;
    }
    const aUrgency = a.days_of_cover !== null && a.days_of_cover !== undefined ? Number(a.days_of_cover) : 999;
    const bUrgency = b.days_of_cover !== null && b.days_of_cover !== undefined ? Number(b.days_of_cover) : 999;
    if (aUrgency !== bUrgency) {
      return aUrgency - bUrgency;
    }
    return Number(b.priority_score || 0) - Number(a.priority_score || 0);
  });
  return ranked[0]?.sku || null;
}

function formatFeedbackWarning(label: string, error: unknown) {
  const message = error instanceof Error && error.message.trim() ? error.message.trim() : "Unknown error";
  return `${label}: ${message}`;
}

function usePlanningWorkspace(): PlanningWorkspaceState {
  const planningVersion = useDomainVersion("planning");
  const { markDirty } = useInvalidation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackWarnings, setFeedbackWarnings] = useState<string[]>([]);
  const [runBuildWarnings, setRunBuildWarnings] = useState<string[]>([]);
  const [cashLimit, setCashLimit] = useState(25000);
  const [currentCashDraft, setCurrentCashDraft] = useState("50000");
  const [runs, setRuns] = useState<RecommendationRunSummary[]>([]);
  const [rows, setRows] = useState<RecommendationRow[]>([]);
  const [runDiff, setRunDiff] = useState<RunDiffResponse | null>(null);
  const [runDiffVersion, setRunDiffVersion] = useState(0);
  const [selectedRunDiffDetail, setSelectedRunDiffDetail] = useState<SkuRunDiffDetail | null>(null);
  const [demandSeries, setDemandSeries] = useState<DemandSeriesRow[]>([]);
  const [projectionRows, setProjectionRows] = useState<ProjectionRow[]>([]);
  const [dashboardWarnings, setDashboardWarnings] = useState<string[]>([]);
  const [dataFreshnessSeconds, setDataFreshnessSeconds] = useState<number | null>(null);
  const [runSourceMode, setRunSourceMode] = useState<string | null>(null);
  const [runHealthStatus, setRunHealthStatus] = useState<string | null>(null);
  const [freshSkuCount, setFreshSkuCount] = useState<number | null>(null);
  const [blockedSkuCount, setBlockedSkuCount] = useState<number | null>(null);
  const [blockedASkuCount, setBlockedASkuCount] = useState<number | null>(null);
  const [feedbackOverview, setFeedbackOverview] = useState<FeedbackOverviewResponse | null>(null);
  const [skuFeedback, setSkuFeedback] = useState<SkuFeedbackDetailResponse | null>(null);
  const [supplierFeedback, setSupplierFeedback] = useState<SupplierFeedbackRow[]>([]);
  const [skuInboundPipeline, setSkuInboundPipeline] = useState<SkuInboundPipeline | null>(null);
  const [skuCostHistory, setSkuCostHistory] = useState<SkuCostHistoryResponse | null>(null);
  const [supplierTerms, setSupplierTerms] = useState<SupplierTerm | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isRunSwitching, setIsRunSwitching] = useState(false);

  const requestedRunId = readNullableStringParam(searchParams, "runId");
  const compareRunId = readStringParam(searchParams, "compareRunId", "auto");
  const tier = readStringParam(searchParams, "tier");
  const risk = readStringParam(searchParams, "risk");
  const supplier = readStringParam(searchParams, "supplier");
  const blockedOnly = readBooleanParam(searchParams, "blockedOnly", false);
  const scenario = readStringParam(searchParams, "scenario", "base");
  const selectedSku = readNullableStringParam(searchParams, "sku");
  const selectedSkuRef = useRef<string | null>(selectedSku);
  const dashboardRequestSeqRef = useRef(0);
  const runDiffRequestSeqRef = useRef(0);
  const skuDetailsRequestSeqRef = useRef(0);
  const runDiffDetailRequestSeqRef = useRef(0);
  const activeDashboardKeyRef = useRef<string | null>(null);
  const activeRunDiffKeyRef = useRef<string | null>(null);
  const suppressedDashboardKeyRef = useRef<string | null>(null);
  const pendingMutationKeysRef = useRef<Record<string, PendingIdempotencyEntry>>({});
  const currentRunDiffRequestKey = useMemo(() => {
    if (!activeRunId) {
      return null;
    }
    return buildRunDiffRequestKey({
      runId: activeRunId,
      compareRunId,
      tier,
      risk,
      supplier,
      blockedOnly,
    });
  }, [activeRunId, compareRunId, tier, risk, supplier, blockedOnly]);
  const parsedCurrentCash = parseNumericDraft(currentCashDraft, "decimal");
  const currentCash = parsedCurrentCash.valid ? parsedCurrentCash.parsed : null;
  const currentCashValid = currentCash !== null;

  useEffect(() => {
    selectedSkuRef.current = selectedSku;
  }, [selectedSku]);

  function patchCurrentSearchParams(updates: Record<string, SearchValue>) {
    setSearchParams((current) => applySearchParamUpdates(current, updates), { replace: true });
  }

  function updateFilters(updates: Record<string, string | boolean | null>) {
    patchCurrentSearchParams(updates);
  }

  function selectSku(sku: string | null) {
    patchCurrentSearchParams({ sku });
  }

  function clearSkuDetails() {
    setSkuFeedback(null);
    setSkuInboundPipeline(null);
    setSkuCostHistory(null);
    setDemandSeries([]);
    setProjectionRows([]);
    setSupplierTerms(null);
  }

  function invalidateSkuDetails() {
    skuDetailsRequestSeqRef.current += 1;
    clearSkuDetails();
  }

  function invalidateRunDiffDetail() {
    runDiffDetailRequestSeqRef.current += 1;
    setSelectedRunDiffDetail(null);
  }

  function invalidateRunDiff(reload = false) {
    runDiffRequestSeqRef.current += 1;
    activeRunDiffKeyRef.current = null;
    setRunDiff(null);
    if (reload) {
      setRunDiffVersion((value) => value + 1);
    }
  }

  async function fetchRuns() {
    const payload = await apiFetch<RunsListResponse>("/runs/recommendations?limit=50");
    return payload.items || [];
  }

  async function fetchFeedbackOverview() {
    return await apiFetch<FeedbackOverviewResponse>("/feedback/overview?window_days=90");
  }

  async function fetchSupplierFeedback() {
    const payload = await apiFetch<SupplierFeedbackListResponse>("/feedback/suppliers?window_days=90");
    return payload.items || [];
  }

  async function fetchDashboard(nextRequestedRunId?: string | null): Promise<DashboardSnapshot> {
    const query = new URLSearchParams();
    if (nextRequestedRunId) query.set("run_id", nextRequestedRunId);
    if (tier) query.set("tier", tier);
    if (risk) query.set("risk", risk);
    if (supplier) query.set("supplier", supplier);
    if (blockedOnly) query.set("blocked", "true");
    const data = await apiFetch<DashboardResponse>(`/dashboard/replenishment?${query.toString()}`);
    return {
      runId: data.run_id,
      rows: data.rows || [],
      warnings: data.warnings || [],
      dataFreshnessSeconds: data.data_freshness_seconds ?? null,
      runSourceMode: data.run_source_mode ?? null,
      runHealthStatus: data.run_health_status ?? null,
      freshSkuCount: data.fresh_sku_count ?? null,
      blockedSkuCount: data.blocked_sku_count ?? null,
      blockedASkuCount: data.blocked_a_sku_count ?? null,
    };
  }

  async function loadRunDiff(activeRunId: string, requestKey: string) {
    const requestId = ++runDiffRequestSeqRef.current;
    const query = new URLSearchParams();
    if (compareRunId !== "auto") query.set("compare_to_run_id", compareRunId);
    query.set("only_changed", "true");
    if (tier) query.set("tier", tier);
    if (risk) query.set("risk", risk);
    if (supplier) query.set("supplier", supplier);
    if (blockedOnly) query.set("blocked", "true");
    try {
      const payload = await apiFetch<RunDiffResponse>(
        `/runs/recommendations/${activeRunId}/diff?${query.toString()}`,
      );
      if (requestId !== runDiffRequestSeqRef.current) {
        return;
      }
      activeRunDiffKeyRef.current = requestKey;
      setRunDiff(payload);
    } catch (err) {
      if (requestId !== runDiffRequestSeqRef.current) {
        return;
      }
      activeRunDiffKeyRef.current = null;
      throw err;
    }
  }

  async function loadRunDiffDetail(activeRunId: string, sku: string, requestKey: string) {
    const requestId = ++runDiffDetailRequestSeqRef.current;
    setSelectedRunDiffDetail(null);

    try {
      const query = new URLSearchParams();
      if (compareRunId !== "auto") query.set("compare_to_run_id", compareRunId);
      if (risk) query.set("risk", risk);
      const payload = await apiFetch<SkuRunDiffDetail>(
        `/runs/recommendations/${activeRunId}/diff/skus/${encodeURIComponent(sku)}?${query.toString()}`,
      );
      if (
        requestId !== runDiffDetailRequestSeqRef.current ||
        activeRunDiffKeyRef.current !== requestKey
      ) {
        return;
      }
      setSelectedRunDiffDetail(payload);
    } catch (err) {
      if (
        requestId !== runDiffDetailRequestSeqRef.current ||
        activeRunDiffKeyRef.current !== requestKey
      ) {
        return;
      }
      throw err;
    }
  }

  async function loadSkuDetails(sku: string, activeRunId: string, activeScenario: string) {
    const requestId = ++skuDetailsRequestSeqRef.current;
    clearSkuDetails();

    try {
      const [demandPayload, inboundPayload, costHistoryPayload, projectionResult, feedbackPayload] =
        await Promise.all([
          apiFetch<DemandSeriesResponse>(`/skus/${encodeURIComponent(sku)}/demand-series?days=90`),
          apiFetch<SkuInboundPipeline>(`/skus/${encodeURIComponent(sku)}/inbound-pipeline`),
          apiFetch<SkuCostHistoryResponse>(`/skus/${encodeURIComponent(sku)}/cost-history`),
          apiFetch<ProjectionResponse>(
            `/runs/recommendations/${activeRunId}/projection?sku=${encodeURIComponent(sku)}&scenario=${activeScenario}`,
          ).catch(() => null),
          apiFetch<SkuFeedbackDetailResponse>(`/feedback/skus/${encodeURIComponent(sku)}?window_days=90`).catch(
            () => null,
          ),
        ]);

      if (requestId !== skuDetailsRequestSeqRef.current) {
        return;
      }

      setDemandSeries(demandPayload.rows || []);
      setSkuInboundPipeline(inboundPayload);
      setSkuCostHistory(costHistoryPayload);
      setProjectionRows(projectionResult?.rows || []);
      setSkuFeedback(feedbackPayload || null);

      const currentRow =
        runDiff?.rows.find((row) => row.sku === sku)?.current ||
        rows.find((row) => row.sku === sku) ||
        runDiff?.rows.find((row) => row.sku === sku)?.previous ||
        null;

      if (!currentRow?.supplier) {
        if (requestId === skuDetailsRequestSeqRef.current) {
          setSupplierTerms(null);
        }
        return;
      }

      try {
        const supplierTerms = await apiFetch<SupplierTerm>(`/suppliers/${encodeURIComponent(currentRow.supplier)}/terms`);
        if (requestId !== skuDetailsRequestSeqRef.current) {
          return;
        }
        setSupplierTerms(supplierTerms);
      } catch {
        if (requestId !== skuDetailsRequestSeqRef.current) {
          return;
        }
        setSupplierTerms(null);
      }
    } catch (err) {
      if (requestId !== skuDetailsRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function refreshPlanning(nextRequestedRunId?: string | null) {
    const requestKey = buildDashboardRequestKey({
      runId: nextRequestedRunId ?? null,
      tier,
      risk,
      supplier,
      blockedOnly,
      planningVersion,
    });

    if (suppressedDashboardKeyRef.current === requestKey) {
      suppressedDashboardKeyRef.current = null;
      return;
    }

    if (activeDashboardKeyRef.current === requestKey) {
      return;
    }

    const requestId = ++dashboardRequestSeqRef.current;
    const shouldShowRunSwitching = activeRunId !== null && nextRequestedRunId !== activeRunId;
    setError(null);
    if (shouldShowRunSwitching) {
      setIsRunSwitching(true);
    }

    const feedbackOverviewPromise = fetchFeedbackOverview();
    const supplierFeedbackPromise = fetchSupplierFeedback();

    async function settleAncillaryFeedback() {
      const [overviewResult, supplierResult] = await Promise.allSettled([
        feedbackOverviewPromise,
        supplierFeedbackPromise,
      ]);

      if (requestId !== dashboardRequestSeqRef.current) {
        return;
      }

      const warnings: string[] = [];

      if (overviewResult.status === "fulfilled") {
        setFeedbackOverview(overviewResult.value);
      } else {
        warnings.push(formatFeedbackWarning("Feedback overview unavailable", overviewResult.reason));
      }

      if (supplierResult.status === "fulfilled") {
        setSupplierFeedback(supplierResult.value);
      } else {
        warnings.push(formatFeedbackWarning("Supplier feedback unavailable", supplierResult.reason));
      }

      setFeedbackWarnings(warnings);
    }

    try {
      const [nextRuns, dashboard] = await Promise.all([
        fetchRuns(),
        fetchDashboard(nextRequestedRunId),
      ]);

      if (requestId !== dashboardRequestSeqRef.current) {
        return;
      }

      const nextSku = normalizeSelectedSku(dashboard.rows, selectedSkuRef.current);

      invalidateRunDiffDetail();
      invalidateSkuDetails();
      setRuns(nextRuns);
      setRows(dashboard.rows);
      setDashboardWarnings(dashboard.warnings);
      setDataFreshnessSeconds(dashboard.dataFreshnessSeconds);
      setRunSourceMode(dashboard.runSourceMode);
      setRunHealthStatus(dashboard.runHealthStatus);
      setFreshSkuCount(dashboard.freshSkuCount);
      setBlockedSkuCount(dashboard.blockedSkuCount);
      setBlockedASkuCount(dashboard.blockedASkuCount);
      setActiveRunId(dashboard.runId);
      invalidateRunDiff(true);
      activeDashboardKeyRef.current = buildDashboardRequestKey({
        runId: dashboard.runId,
        tier,
        risk,
        supplier,
        blockedOnly,
        planningVersion,
      });
      patchCurrentSearchParams({ runId: dashboard.runId, sku: nextSku });
      void settleAncillaryFeedback();
    } catch (err) {
      if (requestId !== dashboardRequestSeqRef.current) {
        return;
      }
      if (activeRunId === null && nextRequestedRunId !== null) {
        suppressedDashboardKeyRef.current = buildDashboardRequestKey({
          runId: null,
          tier,
          risk,
          supplier,
          blockedOnly,
          planningVersion,
        });
      }
      patchCurrentSearchParams({ runId: activeRunId, sku: selectedSkuRef.current });
      throw err;
    } finally {
      if (requestId === dashboardRequestSeqRef.current) {
        setIsRunSwitching(false);
      }
    }
  }

  async function handleRunPlanning() {
    if (currentCash === null) {
      return;
    }
    setLoading(true);
    setError(null);
    setRunBuildWarnings([]);
    const requestBody = JSON.stringify({
      available_cash_now: currentCash,
      enable_transfer_planning: true,
      enable_supplier_batching: true,
    });
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      "planning-run",
      requestBody,
    );
    try {
      const data = await apiFetch<{ run_id: string }>("/planning/run", {
        method: "POST",
        body: requestBody,
        headers: { "Idempotency-Key": key },
      });
      markDirty(["procurement", "operations", "finance", "feedback"]);
      try {
        await refreshPlanning(data.run_id);
        clearPendingIdempotencyKey(pendingMutationKeysRef.current, "planning-run");
      } catch {
        setRunBuildWarnings([RUN_BUILD_WARNING_MESSAGE]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleWhatIf() {
    if (currentCash === null) {
      return;
    }
    setLoading(true);
    setError(null);
    setRunBuildWarnings([]);
    const requestBody = JSON.stringify({
      cash_limit: cashLimit,
      available_cash_now: currentCash,
      enable_transfer_planning: true,
      enable_supplier_batching: true,
    });
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      "planning-what-if",
      requestBody,
    );
    try {
      const data = await apiFetch<{ run_id: string }>("/planning/what-if", {
        method: "POST",
        body: requestBody,
        headers: { "Idempotency-Key": key },
      });
      markDirty(["procurement", "operations", "finance", "feedback"]);
      try {
        await refreshPlanning(data.run_id);
        clearPendingIdempotencyKey(pendingMutationKeysRef.current, "planning-what-if");
      } catch {
        setRunBuildWarnings([RUN_BUILD_WARNING_MESSAGE]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshPlanning(requestedRunId).catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningVersion, requestedRunId, tier, risk, supplier, blockedOnly]);

  useEffect(() => {
    if (!activeRunId) {
      invalidateRunDiff();
      return;
    }
    if (!currentRunDiffRequestKey) {
      invalidateRunDiff();
      return;
    }
    loadRunDiff(activeRunId, currentRunDiffRequestKey).catch((err) => setError((err as Error).message));
  }, [activeRunId, currentRunDiffRequestKey, runDiffVersion]);

  useEffect(() => {
    if (activeRunId && compareRunId === activeRunId) {
      patchCurrentSearchParams({ compareRunId: "auto" });
    }
  }, [activeRunId, compareRunId]);

  useEffect(() => {
    if (!selectedSku || !activeRunId) {
      invalidateSkuDetails();
      return;
    }
    loadSkuDetails(selectedSku, activeRunId, scenario).catch((err) => setError((err as Error).message));
  }, [selectedSku, activeRunId, scenario]);

  useEffect(() => {
    if (
      !selectedSku ||
      !activeRunId ||
      !runDiff?.compare_run ||
      !currentRunDiffRequestKey ||
      activeRunDiffKeyRef.current !== currentRunDiffRequestKey ||
      !runDiff.rows.some((row) => row.sku === selectedSku)
    ) {
      invalidateRunDiffDetail();
      return;
    }
    loadRunDiffDetail(activeRunId, selectedSku, currentRunDiffRequestKey).catch((err) =>
      setError((err as Error).message),
    );
  }, [runDiff, activeRunId, selectedSku, currentRunDiffRequestKey]);

  return {
    loading,
    error,
    feedbackWarnings,
    runBuildWarnings,
    requestedRunId,
    activeRunId,
    isRunSwitching,
    compareRunId,
    tier,
    risk,
    supplier,
    blockedOnly,
    scenario,
    selectedSku,
    cashLimit,
    currentCashDraft,
    currentCashValid,
    runs,
    rows,
    runDiff,
    selectedRunDiffDetail,
    demandSeries,
    projectionRows,
    dashboardWarnings,
    dataFreshnessSeconds,
    runSourceMode,
    runHealthStatus,
    freshSkuCount,
    blockedSkuCount,
    blockedASkuCount,
    feedbackOverview,
    skuFeedback,
    supplierFeedback,
    skuInboundPipeline,
    skuCostHistory,
    supplierTerms,
    handleRunPlanning,
    handleWhatIf,
    setCashLimit,
    setCurrentCashDraft,
    setScenario: (value) => patchCurrentSearchParams({ scenario: value }),
    updateFilters,
    selectSku,
  };
}

export function PlanningWorkspace() {
  const navigate = useNavigate();
  const state = usePlanningWorkspace();

  const detailCurrentRow = useMemo(
    () => state.runDiff?.rows.find((row) => row.sku === state.selectedSku)?.current || state.rows.find((row) => row.sku === state.selectedSku) || null,
    [state.rows, state.runDiff?.rows, state.selectedSku],
  );
  const selectedDiffRow = useMemo(
    () => state.runDiff?.rows.find((row) => row.sku === state.selectedSku) || null,
    [state.runDiff?.rows, state.selectedSku],
  );
  const detailRow = useMemo(
    () => detailCurrentRow || selectedDiffRow?.previous || state.selectedRunDiffDetail?.previous || null,
    [detailCurrentRow, selectedDiffRow, state.selectedRunDiffDetail?.previous],
  );
  const showRunDiffMode = Boolean(state.runDiff?.compare_run);
  const selectedSupplierFeedback = useMemo(() => {
    if (!detailRow?.supplier) return null;
    return state.supplierFeedback.find((row) => row.supplier === detailRow.supplier) || null;
  }, [detailRow?.supplier, state.supplierFeedback]);

  const detailWarnings = useMemo(() => {
    const warnings: string[] = [];
    if ((detailRow?.fallback_inbound_units ?? 0) > 0) {
      warnings.push("Projection includes fallback inbound ETA because no dated inbound receipt exists.");
    }
    // Zero remains a meaningful planning risk signal here; only nullish values should be treated as missing.
    if (
      detailRow?.inventory_confidence_score !== null &&
      detailRow?.inventory_confidence_score !== undefined &&
      Number(detailRow.inventory_confidence_score) < 0.6
    ) {
      warnings.push("Inventory confidence is low. Demand planning is relying on sparse inventory history.");
    }
    if (state.selectedRunDiffDetail?.warnings?.length) {
      warnings.push(...state.selectedRunDiffDetail.warnings);
    }
    return warnings;
  }, [detailRow, state.selectedRunDiffDetail]);

  const freshnessWarnings = [
    ...(state.dataFreshnessSeconds !== null && state.dataFreshnessSeconds > 36 * 3600
      ? [`Planning data is stale (${Math.floor(state.dataFreshnessSeconds / 3600)}h old)`]
      : []),
    ...state.dashboardWarnings,
  ];
  const attentionSummary = (() => {
    const blocked = state.rows.filter((row) => row.blocked).slice(0, 3);
    const unfunded = state.rows.filter((row) => !row.blocked && !row.funded && Number(row.recommended_qty || 0) > 0).slice(0, 3);
    const urgent = state.rows
      .filter((row) => !row.blocked && row.days_of_cover !== null && row.days_of_cover !== undefined)
      .sort((a, b) => Number(a.days_of_cover || 999) - Number(b.days_of_cover || 999))
      .slice(0, 3);
    return { blocked, unfunded, urgent };
  })();
  const guidedWarnings = [
    ...detailWarnings,
    ...state.runBuildWarnings,
    ...state.feedbackWarnings,
    ...freshnessWarnings,
  ].slice(0, 6);

  return (
    <>
      <SectionIntro
        eyebrow="Planning"
        title="What should I buy next?"
        description="See the most important buys first, understand why they matter, and move quickly without getting lost in raw model detail."
      />
      <WarningPanel title="Things to review" warnings={guidedWarnings} />

      <Panel className="status-strip">
        <div className="status-chip">
          <strong>Run Health</strong>
          <span className={`badge badge-${state.runHealthStatus || "neutral"}`}>{state.runHealthStatus || "-"}</span>
        </div>
        <div className="status-chip">
          <strong>Source Mode</strong>
          <span className="badge badge-neutral">{state.runSourceMode || "-"}</span>
        </div>
        <div className="status-chip">
          <strong>Fresh SKUs</strong>
          <span>{state.freshSkuCount ?? "-"}</span>
        </div>
        <div className="status-chip">
          <strong>Blocked SKUs</strong>
          <span>{state.blockedSkuCount ?? "-"}</span>
        </div>
        <div className="status-chip">
          <strong>Blocked A SKUs</strong>
          <span>{state.blockedASkuCount ?? "-"}</span>
        </div>
      </Panel>

      {state.feedbackOverview && (
        <Panel className="status-strip">
          {[30, 90].map((window) => {
            const metric = state.feedbackOverview?.windows[String(window)];
            if (!metric) return null;
            return (
              <div key={`feedback-window-${window}`} className="status-chip wide-chip">
                <strong>{window}d Accuracy</strong>
                <span>
                  Velocity {formatPct(metric.avg_abs_velocity_bias_pct)} abs bias | Orders{" "}
                  {formatPct(metric.order_on_time_rate)} on-time | Early stockouts {metric.early_stockout_count} |
                  Late arrivals {metric.late_arrival_count} | Cash {formatPct(metric.cash_balance_bias_pct)} bias
                </span>
              </div>
            );
          })}
          <div className="status-chip">
            <strong>Open Suggestions</strong>
            <span>{state.feedbackOverview.open_suggestions.length}</span>
          </div>
        </Panel>
      )}

      {state.rows.length > 0 && !showRunDiffMode && (
        <Panel>
          <h3>What needs your attention first</h3>
          <div className="detail-grid">
            <div className="detail-card">
              <strong>Urgent coverage risk</strong>
              <ul className="summary-list">
                {attentionSummary.urgent.length > 0 ? attentionSummary.urgent.map((row) => (
                  <li key={`urgent-${row.sku}`}>{row.sku}: {row.days_of_cover !== null && row.days_of_cover !== undefined ? Number(row.days_of_cover).toFixed(1) : '-'} days cover, stockout {row.stockout_date || '-'}</li>
                )) : <li>No urgent coverage risks surfaced.</li>}
              </ul>
            </div>
            <div className="detail-card">
              <strong>Unfunded buy recommendations</strong>
              <ul className="summary-list">
                {attentionSummary.unfunded.length > 0 ? attentionSummary.unfunded.map((row) => (
                  <li key={`unfunded-${row.sku}`}>{row.sku}: {row.recommended_qty} buy units deferred ({row.defer_reason || 'deferred'})</li>
                )) : <li>No high-signal unfunded buys surfaced.</li>}
              </ul>
            </div>
            <div className="detail-card">
              <strong>Blocked recommendations</strong>
              <ul className="summary-list">
                {attentionSummary.blocked.length > 0 ? attentionSummary.blocked.map((row) => (
                  <li key={`blocked-${row.sku}`}>{row.sku}: blocked because {row.freshness_block_reason || row.defer_reason || 'review required'}</li>
                )) : <li>No blocked recommendations surfaced.</li>}
              </ul>
            </div>
          </div>
        </Panel>
      )}

      <Panel className="workspace-control-panel recommended-action-panel">
        <h3>Recommended next step</h3>
        <p className="muted-small">
          Start by refreshing the plan, then open the most urgent buy or the top blocked item that needs review.
        </p>
        <div className="actions">
          <button className="button-primary-action" onClick={() => state.handleRunPlanning()} disabled={state.loading || !state.currentCashValid}>
            Refresh Plan
          </button>
          {attentionSummary.urgent[0] && (
            <button type="button" className="button-secondary" onClick={() => state.selectSku(attentionSummary.urgent[0].sku)}>
              Open Top Urgent SKU
            </button>
          )}
          {attentionSummary.blocked[0] && (
            <button type="button" className="button-secondary" onClick={() => state.selectSku(attentionSummary.blocked[0].sku)}>
              Review Top Blocked SKU
            </button>
          )}
        </div>
      </Panel>

      {state.runDiff && (
        <Panel className="status-strip">
          <div className="status-chip wide-chip">
            <strong>Run Diff</strong>
            <span>
              {state.runDiff.compare_run
                ? `Comparing ${new Date(state.runDiff.current_run.run_at).toLocaleString()} against ${new Date(
                    state.runDiff.compare_run.run_at,
                  ).toLocaleString()}`
                : "No comparable prior run found for the selected baseline mode."}
            </span>
          </div>
          {state.runDiff.compare_run && (
            <>
              <div className="status-chip">
                <strong>Changed</strong>
                <span>{state.runDiff.summary.changed}</span>
              </div>
              <div className="status-chip">
                <strong>Added / Removed</strong>
                <span>
                  {state.runDiff.summary.added} / {state.runDiff.summary.removed}
                </span>
              </div>
              <div className="status-chip">
                <strong>Blocked / Unblocked</strong>
                <span>
                  {state.runDiff.summary.blocked} / {state.runDiff.summary.unblocked}
                </span>
              </div>
              <div className="status-chip">
                <strong>Qty Up / Down</strong>
                <span>
                  {state.runDiff.summary.qty_up} / {state.runDiff.summary.qty_down}
                </span>
              </div>
            </>
          )}
        </Panel>
      )}

      <WarningPanel
        title="Comparison Warnings"
        warnings={state.runDiff?.comparison_resolution.warnings || []}
      />

      {state.runDiff?.compare_run && (
        <Panel className="status-strip">
          <div className="status-chip wide-chip">
            <strong>Top Buy Deltas</strong>
            <span>
              {state.runDiff.summary.top_residual_buy_deltas.length > 0
                ? state.runDiff.summary.top_residual_buy_deltas
                    .map((item) => `${item.sku} ${item.previous_label || "-"} -> ${item.current_label || "-"} (${item.delta_label})`)
                    .join(" | ")
                : "No residual buy movement."}
            </span>
          </div>
          <div className="status-chip wide-chip">
            <strong>Top Stockout Shifts</strong>
            <span>
              {state.runDiff.summary.top_stockout_shifts.length > 0
                ? state.runDiff.summary.top_stockout_shifts
                    .map((item) => `${item.sku} ${item.previous_label || "-"} -> ${item.current_label || "-"} (${item.delta_label})`)
                    .join(" | ")
                : "No stockout-date shifts."}
            </span>
          </div>
        </Panel>
      )}

      <Panel className="controls workspace-control-panel">
        <p className="muted-small">Most users only need the latest run, current cash, and a single refresh. Open advanced options only when you want to compare or filter.</p>
        <label>
          Run
          <select
            value={state.requestedRunId || ""}
            onChange={(event) => state.updateFilters({ runId: event.target.value || null })}
          >
            <option value="">Latest</option>
            {state.requestedRunId && !state.runs.some((run) => run.run_id === state.requestedRunId) && (
              <option value={state.requestedRunId}>Selected source run ({state.requestedRunId.slice(0, 8)})</option>
            )}
            {state.runs.map((run) => (
              <option key={run.run_id} value={run.run_id}>
                {new Date(run.run_at).toLocaleString()} ({run.run_type}, {run.calculation_version})
              </option>
            ))}
          </select>
          {state.isRunSwitching && <span className="muted-small">Loading selected run...</span>}
        </label>
        <label>
          Current Cash
          <input
            type="number"
            step="0.01"
            value={state.currentCashDraft}
            onChange={(event) => state.setCurrentCashDraft(event.target.value)}
          />
        </label>
        <button onClick={() => state.handleRunPlanning()} disabled={state.loading || !state.currentCashValid}>
          Refresh Plan
        </button>
        <details className="workspace-details">
          <summary>Advanced planning options</summary>
          <div className="form-row">
        <label>
          Compare To
          <select value={state.compareRunId} onChange={(event) => state.updateFilters({ compareRunId: event.target.value })}>
            <option value="auto">Auto previous comparable</option>
            {state.runs
              .filter((run) => !state.activeRunId || run.run_id !== state.activeRunId)
              .map((run) => (
                <option key={`compare-${run.run_id}`} value={run.run_id}>
                  {new Date(run.run_at).toLocaleString()} ({run.run_type}, {run.calculation_version})
                </option>
              ))}
          </select>
        </label>
        <label>
          Tier
          <select value={state.tier} onChange={(event) => state.updateFilters({ tier: event.target.value })}>
            <option value="">All</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
        </label>
        <label>
          Risk
          <select value={state.risk} onChange={(event) => state.updateFilters({ risk: event.target.value })}>
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="blocked">Blocked</option>
          </select>
        </label>
        <label>
          Supplier
          <input value={state.supplier} onChange={(event) => state.updateFilters({ supplier: event.target.value })} placeholder="optional" />
        </label>
        <label className="checkbox-label">
          <span>Blocked Only</span>
          <input
            type="checkbox"
            checked={state.blockedOnly}
            onChange={(event) => state.updateFilters({ blockedOnly: event.target.checked })}
          />
        </label>
        <label>
          Scenario
          <select value={state.scenario} onChange={(event) => state.setScenario(event.target.value)}>
            <option value="base">Base</option>
            <option value="conservative_demand">Conservative Demand</option>
            <option value="delayed_inbound">Delayed Inbound</option>
          </select>
        </label>
          </div>
        </details>
      </Panel>

      <Panel className="what-if workspace-control-panel">
        <h2>What-if Cash Limit</h2>
        <div className="slider-wrap">
          <input
            type="range"
            min={0}
            max={200000}
            step={1000}
            value={state.cashLimit}
            onChange={(event) => state.setCashLimit(Number(event.target.value))}
          />
          <span>${state.cashLimit.toLocaleString()}</span>
          <button onClick={() => state.handleWhatIf()} disabled={state.loading || !state.currentCashValid}>
            Recalculate Allocation
          </button>
          {state.activeRunId && (
            <a href={buildApiUrl(`/planning/export.csv?run_id=${state.activeRunId}`)} target="_blank" rel="noreferrer">
              Export CSV
            </a>
          )}
        </div>
      </Panel>

      <Panel>
        <h2>Recommended buys</h2>
        <p className="muted-small">The default view is trimmed to the fields that matter most for deciding what to buy, when to buy it, and how much cash it will use.</p>
        {!showRunDiffMode && state.rows.length === 0 && (
          <EmptyState
            title="No buys are ready yet"
            message="Refresh the plan to pull the latest recommendation list. If nothing appears after that, the current inputs may not need an immediate buy."
            compact
            actionLabel="Refresh Plan"
            onAction={() => void state.handleRunPlanning()}
          />
        )}
        <DataTable>
          {showRunDiffMode ? (
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Supplier</th>
                  <th>State</th>
                  <th>Why</th>
                  <th>Tier</th>
                  <th>Raw Qty</th>
                  <th>Transfer Qty</th>
                  <th>Residual Buy Qty</th>
                  <th>Stockout</th>
                  <th>Order-by</th>
                  <th>Freshness / Source</th>
                  <th>Inbound</th>
                  <th>Supplier Batch</th>
                  <th>Transfer Batch</th>
                  <th>Funding</th>
                </tr>
              </thead>
              <tbody>
                {(state.runDiff?.rows || []).map((row) => {
                  const current = row.current;
                  const previous = row.previous;
                  const base = current || previous;
                  return (
                    <tr
                      key={`diff-${row.sku}`}
                      className={row.sku === state.selectedSku ? "selected-row" : ""}
                      onClick={() => state.selectSku(row.sku)}
                    >
                      <td>{row.sku}</td>
                      <td>{base?.supplier || row.supplier || "-"}</td>
                      <td>
                        <span className={`badge badge-${row.state === "blocked" ? "warning" : "neutral"}`}>{row.state}</span>
                      </td>
                      <td>
                        <div className="badge-stack">
                          {row.reasons.slice(0, 3).map((reason) => (
                            <span key={`${row.sku}-${reason.reason_type}`} className="badge badge-neutral">
                              {reason.reason_type}
                            </span>
                          ))}
                        </div>
                        <div className="muted-small">{summarizeDiffReasons(row.reasons, 2) || "-"}</div>
                      </td>
                      <td>
                        <div>{previous?.tier || "-"}</div>
                        <div className="muted-small">now {current?.tier || "-"}</div>
                      </td>
                      <td>
                        <div>{row.previous_values?.raw_reorder_qty_units ?? "-"}</div>
                        <div className="muted-small">
                          now {row.current_values?.raw_reorder_qty_units ?? "-"} ({formatSignedInt(row.raw_reorder_qty_delta_units)})
                        </div>
                      </td>
                      <td>
                        <div>{row.previous_values?.transfer_selected_qty_units ?? "-"}</div>
                        <div className="muted-small">
                          now {row.current_values?.transfer_selected_qty_units ?? "-"} (
                          {formatSignedInt(row.transfer_selected_qty_delta_units)})
                        </div>
                      </td>
                      <td>
                        <div>{row.previous_values?.reorder_qty_units ?? "-"}</div>
                        <div className="muted-small">
                          now {row.current_values?.reorder_qty_units ?? "-"} ({formatSignedInt(row.reorder_qty_delta_units)})
                        </div>
                      </td>
                      <td>
                        <div>{formatDate(row.previous_values?.expected_stockout_date)}</div>
                        <div className="muted-small">
                          now {formatDate(row.current_values?.expected_stockout_date)} ({formatSignedDays(row.stockout_shift_days)})
                        </div>
                      </td>
                      <td>
                        <div>{formatDate(row.previous_values?.order_by_date)}</div>
                        <div className="muted-small">
                          now {formatDate(row.current_values?.order_by_date)} ({formatSignedDays(row.order_by_shift_days)})
                        </div>
                      </td>
                      <td>
                        <div className="badge-stack">
                          <span className={`badge badge-${row.previous_values?.freshness_status || "neutral"}`}>
                            was {row.previous_values?.freshness_status || "-"}
                          </span>
                          <span className={`badge badge-${row.current_values?.freshness_status || "neutral"}`}>
                            now {row.current_values?.freshness_status || "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div>
                          delay {row.previous_values?.inbound_delay_days ?? "-"}d {"->"}{" "}
                          {row.current_values?.inbound_delay_days ?? "-"}d
                        </div>
                        <div className="muted-small">
                          sellable {formatDate(row.previous_values?.next_sellable_date)} {"->"}{" "}
                          {formatDate(row.current_values?.next_sellable_date)}
                        </div>
                      </td>
                      <td>
                        <div>{row.previous_values?.supplier_batch_status || "-"}</div>
                        <div className="muted-small">now {row.current_values?.supplier_batch_status || "-"}</div>
                      </td>
                      <td>
                        <div>{row.previous_values?.transfer_batch_status || "-"}</div>
                        <div className="muted-small">now {row.current_values?.transfer_batch_status || "-"}</div>
                      </td>
                      <td>
                        <div>{row.previous_values?.funded ? "funded" : row.previous_values?.defer_reason || "deferred"}</div>
                        <div className="muted-small">
                          now {row.current_values?.funded ? "funded" : row.current_values?.defer_reason || "deferred"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Supplier</th>
                  <th>Tier</th>
                  <th>Available</th>
                  <th>Inbound</th>
                  <th>Origin Stock</th>
                  <th>Data</th>
                  <th>Health</th>
                  <th>Sales Pace</th>
                  <th>Days Left</th>
                  <th>Risk Date</th>
                  <th>Target</th>
                  <th>Planner Qty</th>
                  <th>Transfer Help</th>
                  <th>Buy Now</th>
                  <th>Order-by</th>
                  <th>Total Cash</th>
                  <th>Cash Now</th>
                  <th>Profit Impact</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {state.rows.map((row) => (
                  <tr
                    key={`${row.sku}-${row.priority_score}`}
                    className={row.sku === state.selectedSku ? "selected-row" : ""}
                    onClick={() => state.selectSku(row.sku)}
                  >
                    <td>{row.sku}</td>
                    <td>{row.supplier || "-"}</td>
                    <td>{row.tier}</td>
                    <td>{row.available}</td>
                    <td>{row.inbound}</td>
                    <td>{row.transfer_origin_key || "-"}</td>
                    <td>
                      <div className="badge-stack">
                        <span className="badge badge-neutral">{row.effective_inventory_source || "-"}</span>
                        <span className="badge badge-neutral">{row.effective_sales_source || "-"}</span>
                      </div>
                    </td>
                    <td>{row.freshness_status || "-"}</td>
                    <td>{Number(row.velocity).toFixed(2)}</td>
                    <td>
                      {row.days_of_cover !== null && row.days_of_cover !== undefined
                        ? Number(row.days_of_cover).toFixed(1)
                        : "-"}
                    </td>
                    <td>{row.stockout_date || "-"}</td>
                    <td>{row.rop_units}</td>
                    <td>{row.raw_recommended_qty ?? "-"}</td>
                    <td>{row.transfer_selected_qty_units ?? row.transfer_candidate_qty_units ?? "-"}</td>
                    <td>{row.recommended_qty}</td>
                    <td>{row.order_by_date || "-"}</td>
                    <td>{formatMoney(row.cash_needed)}</td>
                    <td>{formatMoney(row.cash_required_now)}</td>
                    <td>{formatMoney(row.profit_protected)}</td>
                    <td>
                      {row.blocked
                        ? `Blocked (${row.freshness_block_reason || "freshness"})`
                        : row.funded
                          ? "Yes"
                          : `No (${row.defer_reason || "deferred"})`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DataTable>
      </Panel>

      {detailRow && (
        <Panel className="detail-grid">
          <div>
            <h2>Why this buy matters: {detailRow.sku}</h2>
            <ul className="summary-list">
              <li>
                {detailRow.recommended_qty > 0
                  ? `Buy ${detailRow.recommended_qty} units to protect coverage before ${detailRow.stockout_date || "the next risk window"}.`
                  : "No immediate buy is being recommended right now."}
              </li>
              <li>
                {detailRow.order_by_date
                  ? `Best action timing: place the order by ${detailRow.order_by_date}.`
                  : "Order timing is still being determined from the latest supply and sales data."}
              </li>
              <li>
                {detailRow.blocked
                  ? `This item is currently blocked because ${detailRow.freshness_block_reason || detailRow.defer_reason || "the data needs review"}.`
                  : detailRow.funded
                    ? "This recommendation is currently funded and ready to move forward."
                    : `This recommendation is not fully funded yet${detailRow.defer_reason ? ` because ${detailRow.defer_reason}` : ""}.`}
              </li>
            </ul>
            <div className="detail-cards">
              <MetricCard label="Why it surfaced" value={detailRow.reorder_trigger_reason || "-"} />
              <MetricCard label="Inbound already on the way" value={`${detailRow.scheduled_inbound_units || 0} units`} />
              <MetricCard label="Backup inbound assumption" value={`${detailRow.fallback_inbound_units || 0} units`} />
              <MetricCard label="Priority" value={Number(detailRow.priority_score).toFixed(3)} />
              <MetricCard
                label="Data used"
                value={`Inv: ${detailRow.effective_inventory_source || "-"} | Sales: ${detailRow.effective_sales_source || "-"}`}
              />
              <MetricCard
                label="Allocation help"
                value={`Qty ${detailRow.allocation_candidate_qty ?? 0} | Value/$ ${
                  detailRow.allocation_value_per_dollar !== null && detailRow.allocation_value_per_dollar !== undefined
                    ? Number(detailRow.allocation_value_per_dollar).toFixed(2)
                    : "-"
                }`}
              />
              <MetricCard label="Data health" value={detailRow.freshness_status || "-"} />
              <MetricCard label="Transfer status" value={detailRow.transfer_batch_status || "-"} />
              <MetricCard label="Supplier status" value={detailRow.supplier_batch_status || "-"} />
              <MetricCard
                label="Planner breakdown"
                value={`${detailRow.raw_recommended_qty ?? 0} raw | ${detailRow.transfer_selected_qty_units ?? 0} transfer | ${detailRow.recommended_qty} buy`}
              />
              <MetricCard
                label="Origin supply"
                value={`now ${detailRow.warehouse_available_units ?? 0} | future ${detailRow.warehouse_future_units ?? 0}`}
              />
            </div>

            {state.selectedRunDiffDetail && showRunDiffMode && (
              <>
                <h3>Run Compare</h3>
                <div className="detail-cards">
                  <MetricCard
                    label="State / Rank"
                    value={`${state.selectedRunDiffDetail.state || "-"} | ${state.selectedRunDiffDetail.previous_rank ?? "-"} -> ${
                      state.selectedRunDiffDetail.current_rank ?? "-"
                    } (${formatSignedInt(state.selectedRunDiffDetail.rank_delta)})`}
                  />
                  <MetricCard
                    label="Stockout / Order-by"
                    value={`${formatDate(state.selectedRunDiffDetail.previous_values?.expected_stockout_date)} / ${formatDate(
                      state.selectedRunDiffDetail.previous_values?.order_by_date,
                    )} -> ${formatDate(state.selectedRunDiffDetail.current_values?.expected_stockout_date)} / ${formatDate(
                      state.selectedRunDiffDetail.current_values?.order_by_date,
                    )}`}
                  />
                  <MetricCard
                    label="Funding"
                    value={`${state.selectedRunDiffDetail.previous_values?.funded ? "funded" : state.selectedRunDiffDetail.previous_values?.defer_reason || "deferred"} -> ${
                      state.selectedRunDiffDetail.current_values?.funded ? "funded" : state.selectedRunDiffDetail.current_values?.defer_reason || "deferred"
                    }`}
                  />
                </div>
              </>
            )}

            {state.skuCostHistory && (
              <>
                <h3>Cost snapshot</h3>
                <div className="detail-cards">
                  <MetricCard label="Planning cost" value={formatMoney(state.skuCostHistory.rollup?.effective_planning_landed_cost || 0)} />
                  <MetricCard
                    label="Actual weighted cost"
                    value={formatMoney(state.skuCostHistory.rollup?.actual_weighted_landed_cost || 0)}
                  />
                  <MetricCard
                    label="Fallback / provisional"
                    value={`${formatMoney(state.skuCostHistory.rollup?.static_fallback_landed_cost || 0)} fallback | ${formatMoney(
                      state.skuCostHistory.rollup?.provisional_component || 0,
                    )} provisional`}
                  />
                  <MetricCard
                    label="Status / confidence"
                    value={`${state.skuCostHistory.rollup?.cost_basis_status || "-"} | ${Number(
                      state.skuCostHistory.rollup?.landed_cost_confidence || 0,
                    ).toFixed(2)}`}
                  />
                </div>
              </>
            )}

            <div className="actions">
              {detailCurrentRow?.transfer_proposal_id && (
                <button
                  onClick={() => navigate(`/operations?transferProposalId=${detailCurrentRow.transfer_proposal_id}`)}
                  disabled={state.loading}
                >
                  Open Transfer Proposal
                </button>
              )}
              {detailCurrentRow?.supplier_order_proposal_id && (
                <button
                  onClick={() => navigate(`/procurement?proposalId=${detailCurrentRow.supplier_order_proposal_id}`)}
                  disabled={state.loading}
                >
                  Open Supplier Proposal
                </button>
              )}
            </div>

            <DataTable slim>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Units Sold</th>
                    <th>Inventory</th>
                    <th>State</th>
                    <th>Excluded</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {state.demandSeries.slice(-14).map((row) => (
                    <tr key={row.demand_date}>
                      <td>{row.demand_date}</td>
                      <td>{row.raw_units_sold}</td>
                      <td>{row.inventory_available ?? "-"}</td>
                      <td>{row.inventory_state}</td>
                      <td>{row.excluded_from_velocity ? row.exclusion_reason || "yes" : "no"}</td>
                      <td>{Number(row.inventory_confidence).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>

            {state.skuFeedback && (
              <>
                <h3>Forecast vs Actual</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Horizon</th>
                        <th>Eval Date</th>
                        <th>Forecast Vel</th>
                        <th>Actual Demand</th>
                        <th>Bias %</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(state.skuFeedback.horizons || []).slice(-9).map((row) => (
                        <tr key={`${row.run_id}-${row.horizon_days}-${row.evaluation_date || "pending"}`}>
                          <td>{row.horizon_days}d</td>
                          <td>{row.evaluation_date || "-"}</td>
                          <td>{Number(row.forecast_planning_velocity || 0).toFixed(2)}</td>
                          <td>{row.actual_demand_velocity?.toFixed(2) || "-"}</td>
                          <td>{row.bias_pct !== null ? formatPct(row.bias_pct) : "-"}</td>
                          <td>{row.evaluation_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>

                <h3>Execution Outcomes</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Order</th>
                        <th>Slip</th>
                        <th>Transfer</th>
                        <th>Stockout</th>
                        <th>Physical</th>
                        <th>Amazon</th>
                        <th>Sellable</th>
                        <th>Lineage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(state.skuFeedback.outcomes || []).slice(0, 8).map((row) => (
                        <tr key={`sku-outcome-${row.run_id}`}>
                          <td>{row.run_id.slice(0, 8)}</td>
                          <td>{row.order_execution_status}</td>
                          <td>{row.order_slip_days ?? "-"}</td>
                          <td>{row.transfer_execution_status || "-"}</td>
                          <td>{row.stockout_status}</td>
                          <td>{row.physical_arrival_status || row.arrival_status}</td>
                          <td>{row.amazon_receiving_status || "-"}</td>
                          <td>{row.sellable_release_status || "-"}</td>
                          <td>
                            {row.lineage_mode}
                            {row.lineage_confidence != null ? ` (${(row.lineage_confidence * 100).toFixed(0)}%)` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>

                {state.skuInboundPipeline && (
                  <>
                    <h3>Origin And Transfer Pipeline</h3>
                    <div className="detail-cards">
                      <MetricCard label="Open Sellable Units" value={state.skuInboundPipeline.open_units} />
                      <MetricCard
                        label="Next Sellable Date"
                        value={formatDate(state.skuInboundPipeline.next_sellable_date || null)}
                      />
                      <MetricCard
                        label="Transfer Confidence"
                        value={
                          state.skuInboundPipeline.transfer_confidence !== null &&
                          state.skuInboundPipeline.transfer_confidence !== undefined
                            ? Number(state.skuInboundPipeline.transfer_confidence).toFixed(2)
                            : "-"
                        }
                      />
                      <MetricCard
                        label="Observed Truth"
                        value={sumTruthStageUnits(state.skuInboundPipeline.truth_summary?.observed_stage_units)}
                      />
                      <MetricCard
                        label="Inferred Truth"
                        value={sumTruthStageUnits(state.skuInboundPipeline.truth_summary?.inferred_stage_units)}
                      />
                      <MetricCard
                        label="Truth Source"
                        value={state.skuInboundPipeline.truth_summary?.dominant_source || "-"}
                      />
                    </div>
                  </>
                )}

                <h3>Adjustment Suggestions</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Current</th>
                        <th>Suggested</th>
                        <th>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(state.skuFeedback.suggestions || []).length > 0 ? (
                        (state.skuFeedback.suggestions || []).map((suggestion) => (
                          <tr key={suggestion.id}>
                            <td>{suggestion.suggestion_type}</td>
                            <td>{suggestion.current_value ?? "-"}</td>
                            <td>{suggestion.suggested_value ?? "-"}</td>
                            <td>{(suggestion.confidence * 100).toFixed(0)}%</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4}>No open SKU-level suggestions.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}
          </div>

          <div>
            <h2>Projection ({state.scenario})</h2>
            <DataTable slim>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Start</th>
                    <th>Demand</th>
                    <th>Inbound</th>
                    <th>End</th>
                    <th>Stockout</th>
                  </tr>
                </thead>
                <tbody>
                  {state.projectionRows.slice(0, 21).map((row) => (
                    <tr key={`${row.projection_date}-${row.scenario}`}>
                      <td>{row.projection_date}</td>
                      <td>{row.starting_available}</td>
                      <td>{Number(row.demand_units).toFixed(2)}</td>
                      <td>{row.inbound_units}</td>
                      <td>{row.ending_available}</td>
                      <td>{row.stockout_flag ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>

            {state.supplierTerms && (
              <div className="detail-card supplier-card">
                <strong>Supplier Terms</strong>
                <span>
                  Deposit {state.supplierTerms.deposit_pct}% | Balance {state.supplierTerms.balance_due_timing} +
                  {state.supplierTerms.balance_due_offset_days}d | Freight {state.supplierTerms.freight_pct_of_cogs}% of COGS
                </span>
              </div>
            )}

            {selectedSupplierFeedback && (
              <div className="detail-card supplier-card">
                <strong>Supplier Feedback</strong>
                <span>
                  Median delay {selectedSupplierFeedback.median_arrival_delay_days ?? "-"}d | On-time{" "}
                  {formatPct(selectedSupplierFeedback.order_on_time_rate)} | Cash warnings{" "}
                  {selectedSupplierFeedback.cash_warning_count}
                </span>
              </div>
            )}

            <details>
              <summary>Run Explainability</summary>
              <pre>{JSON.stringify(detailRow.explainability || {}, null, 2)}</pre>
            </details>
          </div>
        </Panel>
      )}

      {state.error && (
        <Panel className="warning-panel">
          <p>{state.error}</p>
        </Panel>
      )}
    </>
  );
}
